"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/atsa/AppShell";
import { ModeSelector } from "@/components/atsa/ModeSelector";
import { Results } from "@/components/atsa/Results";
import { TestChrome } from "@/components/atsa/TestChrome";
import { ATSA_PROFILE_2026, type PracticeMode } from "@/lib/atsa/atsaConfig";
import { sideFromView, type Point } from "@/lib/atsa/spatial";
import { clamp, randomInt, sample } from "@/lib/atsa/utils";

type Part = 1 | 2;
type Phase = "options" | "running" | "result";
type Geometry = "clear" | "far" | "edge" | "overlap" | "near-collinear";

type Scenario = {
  heading: number;
  large: Point;
  small: Point;
  eye: Point | null;
  prompt: "LEFT" | "RIGHT";
  truth: boolean;
  actual: "LEFT" | "RIGHT";
  smallHeading: number;
  geometry: Geometry;
};

type Response = { part: Part; correct: boolean; missed: boolean; reactionMs: number; geometry: Geometry; eye: boolean };

const WIDTH = 960;
const HEIGHT = 600;

function makeScenario(part: Part, eyeProbability: number): Scenario {
  for (let attempt = 0; attempt < 200; attempt += 1) {
    const geometry = sample<Geometry>(["clear", "clear", "far", "edge", "overlap", "near-collinear"]);
    const heading = randomInt(0, 23) * 15;
    const radians = (heading * Math.PI) / 180;
    const forward = { x: Math.sin(radians), y: -Math.cos(radians) };
    const right = { x: Math.cos(radians), y: Math.sin(radians) };
    let large = { x: randomInt(230, 730), y: randomInt(180, 410) };
    if (geometry === "edge") {
      large = {
        x: sample([randomInt(150, 230), randomInt(730, 810)]),
        y: sample([randomInt(130, 210), randomInt(390, 470)]),
      };
    }
    const side = Math.random() < 0.5 ? -1 : 1;
    const lateral = geometry === "near-collinear"
      ? randomInt(20, 42)
      : geometry === "overlap"
        ? randomInt(28, 58)
        : geometry === "far"
          ? randomInt(200, 300)
          : randomInt(105, 205);
    const longitudinal = geometry === "overlap"
      ? randomInt(-45, 45)
      : geometry === "near-collinear"
        ? sample([randomInt(-245, -135), randomInt(135, 245)])
        : geometry === "far"
          ? randomInt(-260, 260)
          : randomInt(-150, 170);
    const small = {
      x: large.x + right.x * side * lateral + forward.x * longitudinal,
      y: large.y + right.y * side * lateral + forward.y * longitudinal,
    };
    if (small.x < 55 || small.x > WIDTH - 55 || small.y < 70 || small.y > HEIGHT - 95) continue;

    let eye: Point | null = null;
    if (part === 2 && Math.random() < eyeProbability) {
      const angle = (randomInt(0, 359) * Math.PI) / 180;
      eye = {
        x: clamp(large.x + Math.cos(angle) * randomInt(230, 330), 45, WIDTH - 45),
        y: clamp(large.y + Math.sin(angle) * randomInt(190, 275), 65, HEIGHT - 100),
      };
      const eyeDistance = Math.hypot(eye.x - large.x, eye.y - large.y);
      if (eyeDistance < 140) continue;
    }

    const origin = eye ?? large;
    const lookAt = eye ? large : { x: large.x + forward.x * 100, y: large.y + forward.y * 100 };
    const actual = sideFromView(origin, lookAt, small);
    const prompt = Math.random() < 0.5 ? "LEFT" : "RIGHT";
    return {
      heading,
      large,
      small,
      eye,
      prompt,
      truth: prompt === actual,
      actual,
      smallHeading: (heading + randomInt(-80, 80) + 360) % 360,
      geometry,
    };
  }
  return makeScenario(part, eyeProbability);
}

function Plane({ x, y, rotation, small = false }: { x: number; y: number; rotation: number; small?: boolean }) {
  // Two distinct, original top-view silhouettes drawn nose-up (toward -y) so the
  // rotation orients the nose and the left/right answer key is unaffected.
  // Large: a swept-wing airliner. Small: a straight-wing high-wing light aircraft.
  if (small) {
    const scale = 0.62;
    return (
      <g transform={`translate(${x} ${y}) rotate(${rotation}) scale(${scale})`} aria-hidden="true">
        {/* straight high wing */}
        <rect x="-54" y="-12" width="108" height="10" rx="3" fill="#c93431" stroke="#9f201e" strokeWidth={2} />
        {/* fuselage */}
        <path d="M0 -42 C3.5 -36 5 -22 5 -6 L5 30 C5 38 2.5 42 0 43 C-2.5 42 -5 38 -5 30 L-5 -6 C-5 -22 -3.5 -36 0 -42Z" fill="#c93431" stroke="#9f201e" strokeWidth={2} />
        {/* horizontal stabilizer */}
        <rect x="-20" y="28" width="40" height="8" rx="2.5" fill="#c93431" stroke="#9f201e" strokeWidth={2} />
      </g>
    );
  }
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotation})`} aria-hidden="true">
      {/* fuselage */}
      <path d="M0 -66 C5 -59 7 -40 7 -16 L7 42 C7 52 4 58 0 60 C-4 58 -7 52 -7 42 L-7 -16 C-7 -40 -5 -59 0 -66Z" fill="#707070" stroke="#565656" strokeWidth={2.4} />
      {/* swept main wings */}
      <path d="M6 -10 L64 26 L64 35 L6 18 Z" fill="#707070" stroke="#565656" strokeWidth={2.4} />
      <path d="M-6 -10 L-64 26 L-64 35 L-6 18 Z" fill="#707070" stroke="#565656" strokeWidth={2.4} />
      {/* swept tailplane */}
      <path d="M5 38 L30 54 L30 60 L5 47 Z" fill="#707070" stroke="#565656" strokeWidth={2.4} />
      <path d="M-5 38 L-30 54 L-30 60 L-5 47 Z" fill="#707070" stroke="#565656" strokeWidth={2.4} />
    </g>
  );
}

function Eye({ x, y, target }: { x: number; y: number; target: Point }) {
  const angle = Math.atan2(target.y - y, target.x - x) * 180 / Math.PI;
  return (
    <g transform={`translate(${x} ${y}) rotate(${angle})`} opacity="0.72">
      <path d="M-30 0 Q0 -20 30 0 Q0 20 -30 0Z" fill="#c9b75f" stroke="#b4a44f" strokeWidth="2" />
      <circle cx="0" cy="0" r="9" fill="#7a7040" />
      <circle cx="-2" cy="-2" r="2.5" fill="#ece8cd" />
    </g>
  );
}

const SPATIAL_PROFILES = [
  { id: "relaxed", label: "Relaxed", tag: "Warm-up", q: 30, win: 2500 },
  { id: "standard", label: "Standard", tag: "Test-like", q: 30, win: 1750, standard: true },
  { id: "fast", label: "Fast", tag: "Harder", q: 30, win: 1250 },
  { id: "sprint", label: "Sprint", tag: "Hardest", q: 30, win: 900 },
] as const;

export default function SpatialAwarenessPage() {
  const [mode, setMode] = useState<PracticeMode>("training");
  const [phase, setPhase] = useState<Phase>("options");
  const [selectedPart, setSelectedPart] = useState<"all" | "1" | "2">("all");
  const [questionsPerPart, setQuestionsPerPart] = useState(15);
  const [responseWindowMs, setResponseWindowMs] = useState(1750);
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);
  const [parts, setParts] = useState<Part[]>([1, 2]);
  const [partIndex, setPartIndex] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [scenario, setScenario] = useState<Scenario>(() => makeScenario(1, 0));
  const [remainingMs, setRemainingMs] = useState(1750);
  const [responses, setResponses] = useState<Response[]>([]);
  const [feedback, setFeedback] = useState<"good" | "bad" | null>(null);
  const [adaptive, setAdaptive] = useState(false);
  const adaptiveWindowRef = useRef(0);
  const recentRef = useRef<boolean[]>([]);
  const advancingRef = useRef(false);
  const shownAtRef = useRef(0);

  const effectiveQuestions = mode === "simulation" ? ATSA_PROFILE_2026.spatial.questionsPerPart : questionsPerPart;
  const effectiveWindow = mode === "simulation" ? ATSA_PROFILE_2026.spatial.responseWindowMs : responseWindowMs;
  const effectiveParts: Part[] = mode === "simulation" ? [1, 2] : selectedPart === "all" ? [1, 2] : [Number(selectedPart) as Part];
  const currentPart = parts[partIndex] ?? 1;
  const totalQuestions = parts.length * effectiveQuestions;
  const completed = partIndex * effectiveQuestions + questionIndex;

  const createForPart = useCallback((part: Part) => makeScenario(part, part === 2 ? ATSA_PROFILE_2026.spatial.eyeProbability : 0), []);

  const begin = useCallback(() => {
    const nextParts = effectiveParts;
    setParts(nextParts);
    setPartIndex(0);
    setQuestionIndex(0);
    setResponses([]);
    setFeedback(null);
    setScenario(createForPart(nextParts[0]!));
    setRemainingMs(effectiveWindow);
    adaptiveWindowRef.current = effectiveWindow;
    recentRef.current = [];
    shownAtRef.current = performance.now();
    advancingRef.current = false;
    setPhase("running");
  }, [createForPart, effectiveParts, effectiveWindow]);

  const advance = useCallback((response?: { correct: boolean; missed: boolean }) => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    const adaptiveActive = adaptive && mode === "training";
    if (response) {
      setResponses((items) => [...items, {
        part: currentPart,
        correct: response.correct,
        missed: response.missed,
        reactionMs: performance.now() - shownAtRef.current,
        geometry: scenario.geometry,
        eye: Boolean(scenario.eye),
      }]);
      const ok = response.correct && !response.missed;
      recentRef.current = [...recentRef.current, ok].slice(-4);
      if (adaptiveActive && recentRef.current.length >= 3) {
        const hits = recentRef.current.filter(Boolean).length;
        const total = recentRef.current.length;
        if (hits === total) adaptiveWindowRef.current = Math.max(700, adaptiveWindowRef.current - 150);
        else if (hits <= total - 2) adaptiveWindowRef.current = Math.min(2500, adaptiveWindowRef.current + 200);
      }
    }
    const nextWindow = adaptiveActive ? adaptiveWindowRef.current : effectiveWindow;
    const delay = mode === "training" && feedbackEnabled && !response?.missed ? 220 : 20;
    window.setTimeout(() => {
      const nextQuestion = questionIndex + 1;
      if (nextQuestion < effectiveQuestions) {
        setQuestionIndex(nextQuestion);
        setScenario(createForPart(currentPart));
        setRemainingMs(nextWindow);
        shownAtRef.current = performance.now();
      } else if (partIndex + 1 < parts.length) {
        const nextPart = parts[partIndex + 1]!;
        setPartIndex((value) => value + 1);
        setQuestionIndex(0);
        setScenario(createForPart(nextPart));
        setRemainingMs(nextWindow);
        shownAtRef.current = performance.now();
      } else {
        setPhase("result");
      }
      advancingRef.current = false;
    }, delay);
  }, [adaptive, createForPart, currentPart, effectiveQuestions, effectiveWindow, feedbackEnabled, mode, partIndex, parts, questionIndex, scenario]);

  useEffect(() => {
    if (phase !== "running") return;
    const interval = window.setInterval(() => {
      setRemainingMs((value) => {
        if (value <= 50) {
          window.clearInterval(interval);
          window.setTimeout(() => advance({ correct: false, missed: true }), 0);
          return 0;
        }
        return value - 50;
      });
    }, 50);
    return () => window.clearInterval(interval);
  }, [advance, phase, scenario]);

  useEffect(() => {
    if (phase !== "running") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat || (event.code !== "KeyT" && event.code !== "KeyF") || advancingRef.current) return;
      event.preventDefault();
      const answer = event.code === "KeyT";
      const correct = answer === scenario.truth;
      if (mode === "training" && feedbackEnabled) {
        setFeedback(correct ? "good" : "bad");
        window.setTimeout(() => setFeedback(null), 200);
      }
      advance({ correct, missed: false });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance, feedbackEnabled, mode, phase, scenario]);

  const correct = responses.filter((response) => response.correct).length;
  const missed = responses.filter((response) => response.missed).length;
  const accuracy = responses.length ? Math.round((correct / responses.length) * 100) : 0;
  const eyeItems = useMemo(() => responses.filter((response) => response.eye), [responses]);
  const eyeAccuracy = eyeItems.length ? Math.round((eyeItems.filter((response) => response.correct).length / eyeItems.length) * 100) : 0;
  const activeSimulation = mode === "simulation" && phase === "running";

  return (
    <AppShell hideChrome={activeSimulation}>
      <TestChrome
        title="Spatial Relationship"
        mode={activeSimulation ? "simulation" : "training"}
        status={phase === "running" ? `Part ${currentPart} of 2 · Question ${completed + 1} of ${totalQuestions}` : undefined}
        onRestart={mode === "training" && phase !== "options" ? begin : undefined}
      >
        {phase === "options" && (
          <section className="panel">
            <div className="panel-header">
              <p className="eyebrow">REPORTED SECTION 3</p>
              <h2>Spatial Relationship</h2>
              <p>Decide whether LEFT or RIGHT is true from the active viewpoint. Part 2 sometimes replaces the large aircraft’s viewpoint with an eye looking toward it.</p>
            </div>
            <ModeSelector value={mode} onChange={(next) => {
              setMode(next);
              if (next === "simulation") {
                setSelectedPart("all");
                setQuestionsPerPart(30);
                setResponseWindowMs(1750);
                setFeedbackEnabled(false);
              }
            }} />
            {mode === "training" && (
              <>
              <div className="profile-block">
                <span className="profile-label">Difficulty profile</span>
                <div className="profile-row">
                  {SPATIAL_PROFILES.map((p) => {
                    const active = !adaptive && questionsPerPart === p.q && responseWindowMs === p.win;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        className={`profile-chip ${active ? "active" : ""}`}
                        onClick={() => { setAdaptive(false); setQuestionsPerPart(p.q); setResponseWindowMs(p.win); }}
                      >
                        <span className="tag">{p.tag}</span>
                        {p.label}
                        {"standard" in p && p.standard ? <span className="std-flag">★ Standard</span> : null}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className={`profile-chip ${adaptive ? "active" : ""}`}
                    onClick={() => { setAdaptive(true); setQuestionsPerPart(30); setResponseWindowMs(1750); }}
                  >
                    <span className="tag">Auto</span>
                    Adaptive
                  </button>
                </div>
                {adaptive ? <small className="field-note">Adaptive shortens the response window after correct streaks and lengthens it after errors. It does not use the replay seed.</small> : null}
              </div>
              <div className="options-grid">
                <div className="control-group">
                  <label>Parts</label>
                  <div className="segmented">
                    {([['all', 'Both'], ['1', 'Part 1'], ['2', 'Part 2']] as const).map(([value, label]) => <button key={value} className={selectedPart === value ? "active" : ""} onClick={() => setSelectedPart(value)}>{label}</button>)}
                  </div>
                </div>
                <div className="control-group">
                  <label htmlFor="spatial-count">Questions per part</label>
                  <select id="spatial-count" value={questionsPerPart} onChange={(event) => setQuestionsPerPart(Number(event.target.value))}><option value={10}>10</option><option value={15}>15</option><option value={30}>30</option></select>
                </div>
                <div className="control-group">
                  <label htmlFor="spatial-window">Response window</label>
                  <select id="spatial-window" value={responseWindowMs} onChange={(event) => setResponseWindowMs(Number(event.target.value))}><option value={2500}>2.5 seconds</option><option value={1750}>1.75 seconds</option><option value={1250}>1.25 seconds</option><option value={900}>0.9 seconds</option></select>
                </div>
                <div className="control-group">
                  <label>Feedback</label>
                  <div className="check-row"><input id="spatial-feedback" type="checkbox" checked={feedbackEnabled} onChange={(event) => setFeedbackEnabled(event.target.checked)} /><label htmlFor="spatial-feedback">Brief correctness flash</label></div>
                </div>
              </div>
              </>
            )}
            <ol className="instructions">
              <li>Press T when the displayed side is true. Press F when it is false.</li>
              <li>Expect edge placements, wide separation, partial overlap, and near-collinear geometry.</li>
              <li>The eye is intentionally lower contrast than most practice products.</li>
              <li>Sixty total questions is the primary evidence profile.</li>
            </ol>
            <button className="primary-button" onClick={begin}>Start {mode === "simulation" ? "Simulation" : "Training"}</button>
          </section>
        )}

        {phase === "running" && (
          <section className={activeSimulation ? "fidelity-spatial" : "spatial-test-panel"}>
            {mode === "training" && <div className="progress-line"><span style={{ width: `${(remainingMs / effectiveWindow) * 100}%` }} /></div>}
            <svg className="spatial-svg" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="Aircraft spatial relationship question">
              <rect width={WIDTH} height={HEIGHT} fill="#e7e7e5" />
              {scenario.eye && <Eye x={scenario.eye.x} y={scenario.eye.y} target={scenario.large} />}
              <Plane x={scenario.large.x} y={scenario.large.y} rotation={scenario.heading} />
              <Plane x={scenario.small.x} y={scenario.small.y} rotation={scenario.smallHeading} small />
              <rect x="310" y="500" width="340" height="72" fill="#fff" stroke="#555" />
              <text x="480" y="550" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="44" fontWeight="700" fill="#111">{scenario.prompt}</text>
            </svg>
            <div className="spatial-key-instruction">Press <kbd>T</kbd> for true or <kbd>F</kbd> for false.</div>
            {feedback && <div className={`feedback floating-feedback ${feedback}`}>{feedback === "good" ? "CORRECT" : `WRONG · ${scenario.actual}`}</div>}
          </section>
        )}

        {phase === "result" && (
          <Results
            section="Spatial Relationship"
            record={{ sectionSlug: "spatial-awareness", score: accuracy, scoreLabel: "Accuracy", detail: { eyeAccuracy } }}
            title="Spatial Relationship Complete"
            metrics={[
              { label: "Correct", value: correct },
              { label: "Incorrect", value: responses.length - correct - missed },
              { label: "Missed", value: missed },
              { label: "Accuracy", value: `${accuracy}%` },
              { label: "Eye-view accuracy", value: eyeItems.length ? `${eyeAccuracy}%` : "N/A" },
              { label: "Responses", value: responses.length },
            ]}
            onAgain={begin}
            onOptions={() => setPhase("options")}
          />
        )}
      </TestChrome>
    </AppShell>
  );
}
